import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ClipboardPen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { myGradingQuery } from '@/features/exams/api'
import { MarkSheetPanel } from '@/features/exams/components/MarkSheetPanel'
import { ProgressMeter } from '@/features/exams/components/ProgressMeter'
import { formatMarks } from '@/features/exams/status'
import { sectionLabel } from '@/features/academic/sectionLabel'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDate } from '@/utils/format'

/** Teacher portal: exams open for marks and the sheets (paper × section) this teacher may grade. */
export function StaffMarksEntryPage() {
  const { t } = useTranslation('exams')
  useDocumentTitle('Marks entry')
  const [params, setParams] = useSearchParams()
  const grading = useQuery(myGradingQuery())
  const examId = Number(params.get('exam') ?? 0)
  const paperId = Number(params.get('paper') ?? 0)
  const sectionId = Number(params.get('section') ?? 0)

  if (grading.isPending) return <LoadingState />
  if (grading.error) return <ErrorState onRetry={() => void grading.refetch()} />

  const current = grading.data
    .flatMap((g) => g.sheets.map((s) => ({ exam: g.exam, sheet: s })))
    .find((x) => x.exam.id === examId && x.sheet.paperId === paperId && x.sheet.sectionId === sectionId)

  if (current) {
    return (
      <>
        <PageHeader
          title={`${current.sheet.subjectName} · ${sectionLabel(current.sheet.className, current.sheet.sectionName, ' – ')}`}
          description={current.exam.name}
          breadcrumbs={[
            { label: t('Marks entry'), to: '/staff/marks' },
            { label: current.sheet.subjectName },
          ]}
          homeHref="/staff"
          actions={
            <Button variant="secondary" leftIcon={<ArrowLeft />} onClick={() => setParams({})}>
              {t('All sheets')}
            </Button>
          }
        />
        <MarkSheetPanel
          key={`${paperId}-${sectionId}`}
          examId={examId}
          paperId={paperId}
          sectionId={sectionId}
        />
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={t('Marks entry')}
        description={t(
          'Exams open for marks, with the sheets you can grade as class teacher or subject teacher.',
        )}
        breadcrumbs={[{ label: t('Marks entry') }]}
        homeHref="/staff"
      />
      {grading.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ClipboardPen />}
            title={t('Nothing to grade right now')}
            description={t('When the office opens marks entry for an exam, your papers appear here.')}
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {grading.data.map((g) => {
            const entered = g.sheets.reduce((n, s) => n + s.marksEntered, 0)
            const expected = g.sheets.reduce((n, s) => n + s.marksExpected, 0)
            return (
              <Card key={g.exam.id}>
                <CardHeader
                  icon={<ClipboardPen />}
                  title={g.exam.name}
                  description={`${g.exam.examType} · ${formatDate(g.exam.startsOn)} – ${formatDate(g.exam.endsOn)}`}
                  actions={
                    <ProgressMeter
                      className="hidden w-48 sm:block"
                      entered={entered}
                      expected={expected}
                      label={t('Your progress')}
                    />
                  }
                />
                <ul className="divide-y divide-line">
                  {g.sheets.map((s) => {
                    const done = s.marksExpected > 0 && s.marksEntered >= s.marksExpected
                    return (
                      <li key={`${s.paperId}-${s.sectionId}`}>
                        <button
                          type="button"
                          onClick={() =>
                            setParams({
                              exam: String(g.exam.id),
                              paper: String(s.paperId),
                              section: String(s.sectionId),
                            })
                          }
                          className="flex w-full flex-col gap-3 px-5 py-3.5 text-left transition-colors hover:bg-surface-muted sm:flex-row sm:items-center"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                              {s.subjectName}
                              <span className="font-normal text-muted">
                                · {sectionLabel(s.className, s.sectionName, ' – ')}
                              </span>
                              {s.isClassTeacher ? (
                                <Badge tone="brand" size="sm">
                                  {t('Your class')}
                                </Badge>
                              ) : null}
                              {s.cover ? (
                                <Badge tone="info" size="sm">
                                  {t('Cover until {{date}}', { date: formatDate(s.cover.until) })}
                                </Badge>
                              ) : null}
                            </p>
                            <p className="text-xs text-muted">
                              {t('Out of {{max}}', { max: formatMarks(s.maxMarks) })}
                              {s.date ? ` · ${t('sat {{date}}', { date: formatDate(s.date) })}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 sm:w-64">
                            <ProgressMeter
                              className="flex-1"
                              entered={s.marksEntered}
                              expected={s.marksExpected}
                              label={`${s.subjectName} ${sectionLabel(s.className, s.sectionName)}`}
                            />
                            {done ? (
                              <Badge tone="success" size="sm">
                                {t('Done')}
                              </Badge>
                            ) : null}
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </Card>
            )
          })}
        </div>
      )}
    </>
  )
}
